import { CheckOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Card, ConfigProvider, Flex, Skeleton, Steps, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInterval } from 'usehooks-ts';
import { Layout_CenterWithSidebar } from '../-components/layout/center-with-sidebar';
import { RedButton } from '../../../../shared/components/button/red-button';
import { matchStatusByName, TaskStatus } from '../../../../shared/constants/task-status';
import { addDetailsToApplicationInProcess, getCurrentApplicationData, getReturningApplicationData, setApplicationHash, setReturningApplicationId } from '../../../../Store/ApplicationsReducer/applicationsActions';
import { setShowReturnApplicationModal } from '../../../../Store/GeneralReducer/generalActions';
import { TaskAppendComment } from './-components/task-append-comment';
import { TaskDetails } from './-components/task-details';
import { TaskReturn } from './-components/task-return';
import { Application_TrackStatus } from './-components/track-status';
import styles from './index.module.css';

const StepTitle = (props) => {
    const { text, offset = 0, style } = props;
    return (
        <Flex style={{ position: 'relative', top: offset, textWrap: 'nowrap', color: '#4D525B', fontWeight: 600, ...(style || {}) }}>
            {text}
        </Flex>
    );
}

const stylePerStep = (step, currentStep, status_uuid) => {
    const currentStatus = TaskStatus[Object.keys(TaskStatus).find(key => TaskStatus[key].uuid === status_uuid)];
    if (!currentStatus) return '#B5B8BE'; // Default color if status
    switch (step) {
        case 0: return currentStep === 0 ? currentStatus.background : currentStep > 0 ? '#4D525B' : '#A6A9AD';
        case 1: return currentStep === 1 ? currentStatus.background : currentStep > 1 ? '#4D525B' : '#A6A9AD';
        case 2: return currentStep === 2 ? currentStatus.background : currentStep > 2 ? '#4D525B' : '#A6A9AD';
        default: return '#B5B8BE'
    }
}

const StepsConfigProvider = (props) => {
    return (
        <ConfigProvider theme={{ components: { Steps: { colorPrimary: '#4D525B', lineWidth: 2 } } }}>
            {props.children}
        </ConfigProvider>
    );
}

export const Application_DeviceId_Uuid = (props) => {
    const match = props.match || {};
    const { deviceId, uuid } = match.params || {};
    const task = useSelector(state => state.applications.currentApplicationData);
    const dispatch = useDispatch();
    const cardRef = useRef(null)
    const matchedStatus = matchStatusByName(task?.status);
    const [loading, setLoading] = useState(false);

    const isDone = [TaskStatus.DONE.uuid].includes(matchedStatus.uuid);
    const needPolling = [TaskStatus.PLANNED.name, TaskStatus.IN_PROGRESS.name, TaskStatus.PAUSED.name].includes(task?.status);

    const refetchTask = useCallback(async () => {
        setLoading(true);
        await dispatch(getCurrentApplicationData(uuid))
        dispatch(setApplicationHash(uuid))
        setLoading(false);
    }, [uuid, dispatch]);

    const [currentStep, setCurrentStep] = useState(0);


    const stepColors = [
        stylePerStep(0, currentStep, TaskStatus.PLANNED.uuid),
        stylePerStep(1, currentStep, matchedStatus.uuid),
        stylePerStep(2, currentStep, matchedStatus.uuid),
    ]

    const stepsItems = [
        {
            title: <StepTitle text="Заявка в обработке" offset={-80} style={{ color: stepColors[0] }} />,
            icon: <Flex style={{ borderRadius: 50, width: 38, height: 38, padding: 4, color: 'white', backgroundColor: stepColors[0] }} justify='center' align='center'><SettingOutlined /></Flex>
        },
        {
            title: <StepTitle text="ИТ-специалист назначен" offset={-80} style={{ left: -28, color: stepColors[1] }} />,
            icon: <Flex style={{ borderRadius: 50, width: 38, height: 38, color: 'white', backgroundColor: stepColors[1], padding: 4, }} justify='center' align='center'><UserOutlined /></Flex>
        },
        {
            title: <StepTitle text={
                matchedStatus.uuid === TaskStatus.CANCELED.uuid
                    ? "Заявка отменена" : "Заявка закрыта"
            } offset={-80} style={{ color: stepColors[2] }} />,
            icon: <Flex style={{ borderRadius: 50, width: 38, height: 38, color: 'white', backgroundColor: stepColors[2], padding: 4, }} justify='center' align='center'><CheckOutlined /></Flex>
        },
    ]

    const appendComment = async ({ comment }) => {
        if (!comment || !comment.trim()) {
            return false;
        }
        setLoading(true);
        await dispatch(addDetailsToApplicationInProcess({ hash: uuid, comment }))
        await refetchTask();
        setLoading(false);
        return true;
    }

    useEffect(() => {
        refetchTask()
        window.scrollTo(0, 0)
    }, [uuid, dispatch])

    useInterval(() => {
        if (needPolling) refetchTask();
    }, (needPolling ? 5 : 60 * 5) * 1000);

    useEffect(() => {
        const { PLANNED, IN_PROGRESS, PAUSED, DONE, CANCELED } = TaskStatus;
        switch (task?.status) {
            case PLANNED.name: setCurrentStep(0); break;
            case IN_PROGRESS.name: case PAUSED.name: setCurrentStep(1); break;
            case DONE.name: case CANCELED.name: setCurrentStep(2); break;
        }
    }, [task?.status]);

    const currentApplicationBackInProgressHandler = () => {
        dispatch(getReturningApplicationData(uuid))
        dispatch(setReturningApplicationId(uuid))
        dispatch(setShowReturnApplicationModal(true));
    }
    return (
        <Layout_CenterWithSidebar {...props}>
            <Card
                ref={cardRef}
                className={styles.app_card}
            >
                <Flex vertical gap={24} className={styles.app_card_body}>
                    {!!task ? (
                        <>
                            <Typography.Title className={styles.app_title} level={2} style={{ margin: 0, color: '#4D4D4F' }}>
                                Детали заявки {task?.humanId}
                            </Typography.Title>
                            <Application_TrackStatus cardRef={cardRef} uuid={uuid} />
                            <StepsConfigProvider>
                                <Steps
                                    className={styles.app_steps}
                                    style={{ position: 'relative', height: 40, marginTop: 30 }}
                                    current={currentStep}
                                    labelPlacement="vertical"
                                    items={stepsItems}
                                />
                            </StepsConfigProvider>
                            <Flex vertical gap={24}>
                                <TaskDetails />
                                {isDone ? (
                                    <Flex justify='end' align='center'>
                                        <RedButton size='large' onClick={() => currentApplicationBackInProgressHandler()}>Вернуть в работу</RedButton>
                                    </Flex>
                                ) : (
                                    <TaskAppendComment onCommentSubmit={appendComment} />
                                )}
                            </Flex>
                        </>
                    ) : (
                        loading ? (
                            <>
                                <Skeleton active paragraph={{ rows: 4 }} />
                            </>
                        ) : (
                            <>
                                <Alert message={
                                    <Typography.Text>
                                        Заявка <b>{uuid}</b> не найдена или была удалена.
                                    </Typography.Text>
                                } type="error" showIcon />
                            </>
                        )
                    )}
                </Flex>
                <TaskReturn />
            </Card>
        </Layout_CenterWithSidebar>
    );
};
